import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function CamdenCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Camden Pothole Claims | 7,300 Defects, 340 Potholes Reported | Fixtyer"
        description="Camden Council addressed over 7,300 highway defects in 2024/25 but reported filling just 340 potholes. See what the DfT AMBER rating means for your claim on borough roads — not TfL red routes."
        canonical="/councils/camden"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Camden', url: '/councils/camden' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Camden spends seventeen times its DfT allocation — can I still claim?',
            answer: 'Yes. Camden earns a GREEN Spend scorecard because it invests far beyond its DfT capital allocation — projected at £7.78m against £444,000 in 2025/26. But the DfT Overall rating is AMBER because road condition is AMBER. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on aggregate spend.',
          },
          {
            question: 'My pothole was on a red route — is Camden responsible?',
            answer: 'Probably not. Camden maintains borough roads only. Transport for London (TfL) maintains London\'s red routes — major arterial roads marked with red lines. Camden\'s transparency report coordinates with TfL on streetworks, but claims for defects on TfL red routes go to TfL, not Camden Council. Check the road classification before you claim.',
          },
          {
            question: 'Camden reported only 340 potholes filled in 2024/25 — is that the full picture?',
            answer: 'No. In the same report Camden states it invested around £1.8m on reactive maintenance and addressed over 7,300 defects following cyclic safety inspections. The published "potholes filled" table counts a narrower category — 340 in 2024/25 versus 7,300+ total defects addressed. Your claim should reference the defect that damaged your vehicle, not whichever label Camden assigns it.',
          },
          {
            question: 'What if my pothole was on a residential street in Camden?',
            answer: 'Unclassified roads make up 211km — 82% of Camden\'s 258km borough road network. At every annual survey from 2020 to 2024, roughly 15% of U-roads were in RED condition — about 32km of residential streets. B and C roads are inspected annually by UKPMS visual surveys, with 35.4% in RED or Amber condition in 2024.',
          },
          {
            question: 'Does Camden\'s central London traffic admission help my claim?',
            answer: 'It can. Camden states its roads "were not specifically designed or built to take current levels of use i.e. the size and weight of modern HGVs" and that carriageway lifespan in central London is reduced to around 16 years owing to exceptionally high usage. That is documented knowledge of accelerated wear — which raises what a "reasonable" inspection regime must look like on heavily trafficked borough routes.',
          },
          {
            question: 'Resurfacing fell to 6.8km in 2024/25 — does that weaken Camden\'s Section 58 defence?',
            answer: 'It depends on your road. Camden resurfaced 4.263 miles (6.812km) in 2024/25, down from 6.47 miles (10.412km) the year before. If your defect formed on a road that was not part of that programme, the council\'s aggregate investment does not automatically prove your specific carriageway was reasonably maintained.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Camden Council"
        councilSlug="camden"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/camden"
        headline="Camden Council Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Camden</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Camden: 7,300 Defects Fixed, 340 Counted as Potholes
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Camden earns <strong className="text-emerald-400">GREEN</strong> for spend — investing around
            <strong className="text-emerald-400"> £7.8m</strong> in capital works against a
            <strong className="text-emerald-400"> £444,000</strong> DfT allocation. Yet the overall DfT rating is
            <strong className="text-amber-400"> AMBER</strong>, and the council's own report reveals a gap that
            matters for claimants: in 2024/25 it addressed
            <strong className="text-rose-400"> over 7,300 defects</strong> through cyclic safety inspections while
            reporting just <strong className="text-rose-400">340 potholes filled</strong> in its DfT table.
          </p>

          {/* Key stat callout */}
          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">7,300+</div>
            <div className="text-white font-medium mt-2">Defects addressed in 2024/25</div>
            <div className="text-brand-300 text-sm mt-1">
              Camden invested around £1.8m on reactive maintenance and addressed over 7,300 defects following
              cyclic safety inspections — while Table 4 records just 340 potholes filled in the same year.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* Borough roads vs TfL */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Borough Roads Only — Not TfL Red Routes
          </h2>
          <p className="text-surface-600 mb-8">
            Camden's transparency data covers the roads it maintains as a London borough
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Camden's 258km Network</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• A-roads: 24.9km (9.7%)</li>
                <li>• B and C roads: 27km (10.5%)</li>
                <li>• Unclassified roads: 211km (81.8%)</li>
                <li>• Cycleways: 20km (reported separately)</li>
                <li>• Footways: 259km</li>
              </ul>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">TfL Red Routes</h3>
              <p className="text-amber-800 text-sm mb-3">
                Camden coordinates streetworks with Transport for London — its report references approximately
                20,000 permit requests per year and active consultation with TfL on bus routes and the Bus Sense
                initiative. But major arterial red routes on TfL's network are maintained by TfL, not Camden.
              </p>
              <p className="text-amber-800 text-sm">
                If your pothole was on a red-route A-road, your claim is likely against TfL. Camden's condition
                data and DfT rating apply to the borough-maintained network above.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="They receive approximately 20,000 permit requests per year which includes permit variations and licence extensions that have to be granted or approved for works to continue on the highway."
            source="Camden Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of survey data from Camden's own transparency report — stable U-road RED levels,
            volatile A-roads, slowly improving B/C roads
          </p>

          {/* A-roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">A-roads (24.9km — 9.7% of network): SCANNER surveyed, volatile</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">22.38%</td>
                    <td className="px-4 py-2 text-center">35.04%</td>
                    <td className="px-4 py-2 text-center">41.64%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">23.38%</td>
                    <td className="px-4 py-2 text-center">28.83%</td>
                    <td className="px-4 py-2 text-center">45.88%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">15.8%</td>
                    <td className="px-4 py-2 text-center">25.1%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">60%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">23.2%</td>
                    <td className="px-4 py-2 text-center">30.3%</td>
                    <td className="px-4 py-2 text-center">45.4%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={3}>Data not yet received</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 text-sm">
              Camden's own footnote: A-road SCANNER data is collected annually, but 2024 submissions had not
              been received at the time of publication. RED A-roads peaked at 23.38% in 2021 — on a network
              where carriageway lifespan is just 16 years in central London.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (27km — 10.5% of network): UKPMS visual inspection, annual</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">18.1%</td>
                    <td className="px-4 py-2 text-center">19%</td>
                    <td className="px-4 py-2 text-center">62.9%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">19.3%</td>
                    <td className="px-4 py-2 text-center">20%</td>
                    <td className="px-4 py-2 text-center">60.7%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">18.7%</td>
                    <td className="px-4 py-2 text-center">19.5%</td>
                    <td className="px-4 py-2 text-center">61.8%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">18.3%</td>
                    <td className="px-4 py-2 text-center">18.6%</td>
                    <td className="px-4 py-2 text-center">63.1%</td>
                  </tr>
                  <tr className="bg-brand-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold">17.8%</td>
                    <td className="px-4 py-2 text-center font-bold">17.6%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">64.6%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              Modest improvement on B/C roads — but 35.4% remain in RED or Amber condition in 2024, meaning
              maintenance may be required soon or should be considered now on over a third of classified local roads.
            </p>
          </div>

          {/* U-roads */}
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-rose-900 mb-4">Unclassified roads (211km — 82% of network): 15% RED, every year</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-rose-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-rose-100 border-b border-rose-200">
                    <th className="px-4 py-2 text-left font-semibold text-rose-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">U-roads in RED condition</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">15.1%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">15.5%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">15.3%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">15.4%</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">15.1%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-rose-800 font-medium">
              Camden reports only the RED percentage for U-roads — not Amber or Green splits. At 15.1% in 2024,
              roughly 32km of residential streets should be considered for maintenance. That figure has barely
              moved in five years.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">GREEN Spend, AMBER Condition</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£444k</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£7.78m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">81%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Camden projects spending roughly <span className="text-emerald-400">seventeen times</span> its DfT
              capital allocation — with 81% classed as preventative. The DfT still rates overall performance
              AMBER because condition and best practice scorecards are both AMBER.
            </p>
          </div>
        </div>
      </section>

      {/* Central London wear */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Sixteen-Year Carriageways in Britain's Densest Borough
          </h2>
          <p className="text-surface-600 mb-8">
            Camden's own explanation for why central London roads fail faster — and why deferring maintenance costs more
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="The lifecycle of an asset in central London, of which Camden is part, is significantly lower than that of other urban, sub-urban or rural areas owing to its exceptionally high usage and density of surrounding population, along with other interferences such as significant utility interventions. Those factors determine that the carriageway lifespan is reduced to around 16 years and the footways around 20 years."
              source="Camden Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="Camden like other Local Authorities have road networks that have evolved over decades and so were not specifically designed or built to take current levels of use i.e. the size and weight of modern HGVs. The roads take a high-level of punishment, which can lead to damage, which is then made worse by severe weather."
              source="Camden Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Annual Deterioration Scale</h3>
              <p className="text-surface-600 text-sm mb-3">
                Camden estimates that each year there is the potential that almost 74,000m² of footways and
                129,000m² of carriageways may annually deteriorate to failure — on a network of approximately
                2 million m² of carriageway and 1.45 million m² of footway surface.
              </p>
              <p className="text-surface-600 text-sm">
                That is documented knowledge of the rate at which assets fail — relevant to what a reasonable
                inspection and repair cycle must achieve under Section 58.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Climate Acceleration</h3>
              <p className="text-amber-800 text-sm mb-3">
                Camden states that general climatic conditions and the changing climate — wetter and warmer
                conditions, drier hotter summers, milder wetter winters, and more extreme rainfall events —
                are "one of the prime causes of deterioration especially in footways."
              </p>
              <p className="text-amber-800 text-sm">
                From 2026/27, a new BSI PAS 2161 methodology will replace the current three-category system
                with five categories — changing how condition is reported nationally.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Potholes vs defects */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            340 Potholes Filled — 7,300 Defects Addressed
          </h2>
          <p className="text-surface-600 mb-8">
            Two different numbers in the same report — and both matter for your claim
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled (Table 4)</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">44</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">301</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">102</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">294</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">340</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold">1,081</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="Last financial year Camden invested around £1.8m on reactive maintenance and there were over 7,300 defects noted across the borough and addressed following the cyclic safety inspections."
            source="Camden Council Local Highways Maintenance Transparency Report 2025"
          />

          <div className="grid md:grid-cols-2 gap-6 mt-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Counting Gap</h3>
              <p className="text-surface-600 text-sm">
                Table 4's "potholes filled" figures — 340 in 2024/25 — sit alongside a separate admission of
                over 7,300 defects addressed through cyclic safety inspections. Camden's contractual
                "find and fix" service notes and repairs minor defects during these inspections. The DfT
                pothole table does not capture the full reactive workload.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">15–20% Reactive, Contractually Bound</h3>
              <p className="text-amber-800 text-sm">
                Camden states that normally around 15–20% of overall spend is on reactive maintenance and
                sustaining the network in safe condition. Reactive maintenance "will continue as it is
                contractually bound and will work towards a defect free network" — an admission that reactive
                repair is built into the system, not an exception.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Resurfacing and spending */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Resurfacing Fell — Capital Spend Held Steady
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of capital and revenue spend, plus network length resurfaced each year
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT allocation</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Preventative / Reactive</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Resurfaced</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">£200,000</td>
                  <td className="px-4 py-3 text-center">£7,030k</td>
                  <td className="px-4 py-3 text-center">£1,200k</td>
                  <td className="px-4 py-3 text-center">86% / 14%</td>
                  <td className="px-4 py-3 text-center">10.23km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">£0</td>
                  <td className="px-4 py-3 text-center">£6,155k</td>
                  <td className="px-4 py-3 text-center">£1,267k</td>
                  <td className="px-4 py-3 text-center">83% / 17%</td>
                  <td className="px-4 py-3 text-center">9.32km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">£200,000</td>
                  <td className="px-4 py-3 text-center">£8,068k</td>
                  <td className="px-4 py-3 text-center">£1,326k</td>
                  <td className="px-4 py-3 text-center">86% / 14%</td>
                  <td className="px-4 py-3 text-center">8.81km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£0</td>
                  <td className="px-4 py-3 text-center">£8,650k</td>
                  <td className="px-4 py-3 text-center">£1,790k</td>
                  <td className="px-4 py-3 text-center">83% / 17%</td>
                  <td className="px-4 py-3 text-center">10.41km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£183,500</td>
                  <td className="px-4 py-3 text-center">£8,359k</td>
                  <td className="px-4 py-3 text-center">£1,800k</td>
                  <td className="px-4 py-3 text-center">83% / 17%</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">6.81km</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">£444,000</td>
                  <td className="px-4 py-3 text-center">£7,780k</td>
                  <td className="px-4 py-3 text-center">£1,825k</td>
                  <td className="px-4 py-3 text-center">81% / 19%</td>
                  <td className="px-4 py-3 text-center text-surface-400 italic">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">2025/26 Forward Programme</h3>
            <p className="text-brand-200 mb-4">
              Camden plans to invest around £7.8m across footways, carriageways, drainage, street lighting and structures:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6 text-sm">
              <li>• Replace around 50,000m² of carriageway surface and return around 40 roads to good order</li>
              <li>• Re-lay around 1,500m² of cobbles across 5 streets plus over 3,000m² of footways</li>
              <li>• Address around 70 non-functioning gullies and replace around 300 street lighting columns</li>
              <li>• Continue works to walls and structures where required</li>
            </ul>
            <p className="text-white font-medium">
              Aggregate investment does not prove your specific road was resurfaced. Resurfacing fell 35% in
              2024/25 — from 10.41km to 6.81km — while defect volumes stayed high.
            </p>
          </div>
        </div>
      </section>

      {/* Section 58 */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Section 58 and Section 41 on Camden's Network
          </h2>
          <p className="text-surface-600 mb-8">
            How Camden's own data maps to the legal tests for pothole damage claims
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Section 41 — The Duty to Maintain</h3>
              <p className="text-surface-600 text-sm mb-3">
                Under Section 41 of the Highways Act 1980, Camden must maintain borough roads that are
                reasonably passable for ordinary traffic. With 15% of U-roads in RED condition every year for
                five years, and up to 23.38% of A-roads in RED at peak, the council's own surveys document
                a persistent maintenance backlog.
              </p>
              <p className="text-surface-600 text-sm">
                Camden's network recovery strategy prioritises "failing, rather than failed assets" — meaning
                roads already in RED are candidates for maintenance, not roads that have been cleared.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Section 58 — The Reasonable Defence</h3>
              <p className="text-amber-800 text-sm mb-3">
                To rely on Section 58, Camden must show it took such care as was reasonable to secure that
                the part of the highway where you were damaged was not dangerous. Cyclic safety inspections
                that noted over 7,300 defects in one year suggest the inspection system is active — but also
                that defects are forming at scale between repairs.
              </p>
              <p className="text-amber-800 text-sm">
                Ask: was your specific defect on an inspection route? Was it noted and left? Was it on a road
                Camden resurfaced that year — or one of the 251km it did not?
              </p>
            </div>
          </div>

          <QuoteCard
            quote="Reactive maintenance is safeguarded through our contractual 'find and fix' service whereby minor defects are noted and repaired following cyclic safety inspections."
            source="Camden Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Balanced claims assessment */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Well-Funded AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Camden is not a failing authority — here is how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — invests far beyond DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 81–86% of spend classed as preventative over five years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented network recovery strategy with data-driven asset management
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> B/C road condition slowly improving — 64.6% Green in 2024
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Active cyclic safety inspections addressing 7,300+ defects annually
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence. Generic claims will struggle on well-maintained classified roads.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — 15% of U-roads in RED every year for five years (~32km)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 7,300+ defects addressed in one year — defects form faster than the pothole table suggests
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Resurfacing fell 35% in 2024/25 — 6.81km versus 10.41km the prior year
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 129,000m² of carriageway may deteriorate to failure annually — documented wear rate
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Roads "not specifically designed" for modern HGV loads — council's own admission
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 20,000 streetwork permits per year — utility interventions accelerating failure
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and a functioning inspection contract, your claim lives or dies on
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same defect (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing size, depth and visible age — weathered edges, previous patching, cobble displacement</li>
              <li>• Road class — 82% of Camden's network is U-roads with 15% persistently RED</li>
              <li>• Whether the road was on Camden's 2024/25 resurfacing programme — only 6.81km of 258km</li>
              <li>• Confirm it was a borough road, not a TfL red route</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Camden's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Camden?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No 7,300-defect context cited</li>
                <li>• No prior-report search</li>
                <li>• No borough vs TfL red route check</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 7,300 defects vs 340 potholes documented</li>
                <li>✅ 15% U-road RED condition cited</li>
                <li>✅ Resurfacing decline and spend data included</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Camden</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=camden"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Camden Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Camden spends seventeen times its DfT allocation — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. Camden earns a GREEN Spend scorecard because it invests far beyond its DfT capital allocation — projected at £7.78m against £444,000 in 2025/26. But the DfT Overall rating is AMBER because road condition is AMBER. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on aggregate spend.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                My pothole was on a red route — is Camden responsible?
              </h3>
              <p className="text-surface-600 text-sm">
                Probably not. Camden maintains borough roads only. Transport for London (TfL) maintains London's red routes — major arterial roads marked with red lines. Camden's transparency report coordinates with TfL on streetworks, but claims for defects on TfL red routes go to TfL, not Camden Council. Check the road classification before you claim.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Camden reported only 340 potholes filled in 2024/25 — is that the full picture?
              </h3>
              <p className="text-surface-600 text-sm">
                No. In the same report Camden states it invested around £1.8m on reactive maintenance and addressed over 7,300 defects following cyclic safety inspections. The published "potholes filled" table counts a narrower category — 340 in 2024/25 versus 7,300+ total defects addressed. Your claim should reference the defect that damaged your vehicle, not whichever label Camden assigns it.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential street in Camden?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 211km — 82% of Camden's 258km borough road network. At every annual survey from 2020 to 2024, roughly 15% of U-roads were in RED condition — about 32km of residential streets. B and C roads are inspected annually by UKPMS visual surveys, with 35.4% in RED or Amber condition in 2024.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Camden's central London traffic admission help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It can. Camden states its roads "were not specifically designed or built to take current levels of use i.e. the size and weight of modern HGVs" and that carriageway lifespan in central London is reduced to around 16 years owing to exceptionally high usage. That is documented knowledge of accelerated wear — which raises what a "reasonable" inspection regime must look like on heavily trafficked borough routes.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Resurfacing fell to 6.8km in 2024/25 — does that weaken Camden's Section 58 defence?
              </h3>
              <p className="text-surface-600 text-sm">
                It depends on your road. Camden resurfaced 4.263 miles (6.812km) in 2024/25, down from 6.47 miles (10.412km) the year before. If your defect formed on a road that was not part of that programme, the council's aggregate investment does not automatically prove your specific carriageway was reasonably maintained.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.camden.gov.uk/documents/d/guest/local-highways-maintenance-transparency-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Camden Council Local Highways Maintenance Transparency Report (24 June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
